<template>

    <container-complex :showSteps="false"
                       @previous="stepPrevious">

        <!--        BODY-->

        <template v-slot:body>


            <div v-if="isStep1">

                <phase :number="1"
                       :title="$t('sign_in_naver')"
                       :subtitle="$t('redirect_new_tab')"
                       :image="isDesktop ? '/img/login/naver/browser.svg' : '/img/login/naver/phone.svg'"/>
                <phase class="mt__8"
                       :number="2"
                       :title="$t('settings_tab')"/>
                <phase class="mt__8"
                       :number="3"
                       :title="$t('select_enable_imap')"/>
                <phase class="mt__8"
                       :number="4"
                       :title="$t('save_return_cleanfox')"/>

            </div>

            <div v-if="isStep2" class="step-2">

                <h2 class="title-baseSemibold">{{ $t('web_login_google_connect_title') }}</h2>

                <img src="/img/login/naver/logo.svg" class="logo">

                <div class="email">
                    <span class="text-sRegular color__moon">{{ $t('web_login_google_connect_field_email') }}</span>
                    <span class="text-baseRegular">{{ email }}</span>
                </div>

                <input-label class="mt__8"
                             :placeholder="$t('login_password_placeholder')"
                             :value="imapPassword"
                             :error="errorPassword"
                             :autocomplete="'current-password'"
                             :type="'password'"
                             @update="updateImapPassword"/>

                <div class="error-login" v-show="errorPassword">
                    <p class="text-baseRegular">{{ $t('naver_app_password_needed') }}</p>
                    <div class="spacer"></div>
                    <button class="cta-small-primary mt__12" @click="stepNext">{{ $t('user_form_cta') }}</button>
                </div>

                <button class="cta-medium-primary mt__32" :disabled="loading" @click="loginIMAP">
                    <lottie-loader v-if="loading" :size="24"></lottie-loader>
                    <span v-else>{{ $t('global_cta_next') }}</span>
                </button>

            </div>

            <div v-if="isStep3">

                <phase :number="1" :title="$t('return_naver')"/>
                <phase :number="2" :title="$t('check_direct')" class="mt__8"/>
                <phase :number="3" :title="$tc('generate_ap', 0, {url: 'https://nid.naver.com/mobile/user/help/2StepVerif'})" :blank="true" class="mt__8" @clickLink="appPasswordLinkClicked"/>
                <phase :number="4" :title="$t('copy_12_car')" :subtitle="$t('web_copy_app_password_subtitle')"
                       class="mt__8"/>

            </div>

            <template v-if="isStep4">

                <p class="text-xlSemibold">{{ $t('connect_to_cleanfox') }}</p>

                <markdown class="text-baseRegular mt__24"
                          :text="$tc('enter_app_password', 0, {email})"></markdown>

                <input-label class="mt__24"
                             :placeholder="$t('login_application_password_placeholder')"
                             :value="appPassword"
                             :error="errorPassword"
                             @update="updateAppPassword"
                            @pressEnter="loginAppPassword"/>

            </template>

        </template>


        <!--        FOOTER-->

        <template v-slot:footer>

            <template v-if="isStep1">

                <button class="cta-medium-secondary cta-full" @click="ctaStep1">
                    <span v-show="!isImapLinkClicked">{{ $t('open_naver') | capitalizeFirstLetter }}</span>
                    <img v-show="!isImapLinkClicked" src="/img/icons/external-link.svg" class="img__24 ml__8">

                    <span v-show="isImapLinkClicked">{{ $t('imap_enabled') }}</span>
                    <img v-show="isImapLinkClicked" src="/img/icons/arrow-right-white.svg" class="img__24 ml__8">
                </button>

            </template>

            <template v-if="isStep3">

                <button class="cta-medium-tertiary" v-if="!isAppPasswordLinkClicked && !isDesktop" @click="stepPrevious">
                    <img src="/img/icons/arrow-left-blue.svg" class="img__24 mr__4">
                    <span class="ml__4">{{ $t('global_previous') }}</span>
                </button>
                <div class="spacer-footer" v-if="!isDesktop"></div>
                <button class="cta-medium-secondary" @click="ctaStep3" :disabled="!isAppPasswordLinkClicked">
                    <span>{{ $t('web_google_code') }}</span>
                    <img src="/img/icons/arrow-right-white.svg" class="img__24 ml__8">
                </button>

            </template>

            <template v-if="isStep4">

                <button class="cta-medium-primary" :disabled="isCtaAppPasswordDisabled" @click="loginAppPassword">
                    <lottie-loader v-if="loading" :size="24"></lottie-loader>
                    <span v-else>{{ $t('login_imap_manual_form_cta') }}</span>
                </button>

            </template>


        </template>

    </container-complex>

</template>

<script>

import ContainerComplex from '../../components/loginFlow/ContainerComplex.vue'
import InputLabel from '../../components/inputs/InputLabel.vue'
import LottieLoader from '../../components/lottie/LottieLoader.vue'
import Markdown from '../../components/Markdown.vue'
import Phase from '../../components/loginFlow/google/Phase.vue'

import Tag from '../../modules/tag'
import Login from '../../modules/login'

export default {
    name: 'naver-login',

    components: {
        LottieLoader,
        ContainerComplex,
        InputLabel,
        Markdown,
        Phase
    },

    props: {
        email: {
            type: String,
            default: ''
        }
    },

    watch: {
        step() {
            this.isImapLinkClicked = false
            this.isAppPasswordLinkClicked = false
            this.errorPassword = ''
        }
    },

    computed: {

        isStep1() {
            return this.step === 1
        },
        isStep2() {
            return this.step === 2
        },
        isStep3() {
            return this.step === 3
        },
        isStep4() {
            return this.step === 4
        },
        isCtaAppPasswordDisabled() {
            return this.appPassword.length !== 12 || this.loading
        }
    },

    data() {
        return {
            step: 0,
            isImapLinkClicked: false,
            isAppPasswordLinkClicked: false,
            provider: 'naver',
            imapPassword: '',
            errorPassword: '',
            appPassword: '',
            loading: false
        }
    },

    mounted() {
        this.stepNext()
    },

    methods: {
        stepNext() {
            if (this.step < 4) {
                this.step++
            }
        },
        stepPrevious() {
            if (this.step > 1) {
                this.step--
            } else {
                this.$router.push({path: '/'})
            }
        },
        ctaStep1() {
            if (this.isImapLinkClicked) {
                Tag.clickAmplitude(this, 'web_connexion_flow_naver_login', this.provider)
                this.stepNext()
            } else {
                this.isImapLinkClicked = true
                window.open('https://mail.naver.com/v2/settings/smtp/imap', '_blank', 'noopener,noreferrer')
                Tag.clickAmplitude(this, 'web_connexion_flow_naver_open', this.provider)
            }
        },
        ctaStep3() {
            if (this.isAppPasswordLinkClicked) {
                Tag.clickAmplitude(this, 'web_connexion_flow_naver_login', this.provider)
                this.stepNext()
            } else {
                this.isAppPasswordLinkClicked = true
                window.open('https://mail.naver.com/v2/settings/smtp/imap', '_blank', 'noopener,noreferrer')
                Tag.clickAmplitude(this, 'web_connexion_flow_naver_open', this.provider)
            }
        },


        login(password) {
            this.loading = true

            Login.connect(this, this.email, password, this.provider)
                .catch(error => {
                    this.errorPassword = this.$t('incorrect_password')
                    this.loading = false
                })
        },
        loginIMAP() {
            this.login(this.imapPassword)
        },
        loginAppPassword() {
            this.login(this.appPassword)
        },
        updateImapPassword(value) {
            this.imapPassword = value
        },
        updateAppPassword(value) {
            this.appPassword = value
        },
        appPasswordLinkClicked() {
            this.isAppPasswordLinkClicked = true
        }
    }
}

</script>

<style lang="scss" scoped>


.step-2 {
    .logo {
        height: 24px;
        margin: 48px 0;
    }

    .error-login {
        margin-top: 16px;
        text-align: start;
        padding: 16px;
        border-radius: 16px;
        background-color: $timberwolf;
        @media screen and (min-width: $above-tablet-length) {
            display: flex;
            flex-direction: row;
        }

        .spacer {
            @media screen and (min-width: $above-tablet-length) {
                min-width: 14px;
            }
        }
    }

    .email {
        padding: 8px 16px;
        border: 2px solid $aquamarine;
        border-radius: 12px;
        display: flex;
        flex-direction: column;
        text-align: start;
    }
}

.spacer-footer {
    min-width: 8px;
}
</style>